/* ==========================================================================
   OPUS ATHLETIC — section behaviour
   1. reveal-on-scroll for .o-rv
   2. transparent header(s) whose scrim follows the surface underneath
   3. training-system card scrollers
   4. in-page anchor links
   Safe to load on every page. No dependencies.
   ========================================================================== */
(function () {
  'use strict';

  /* ---------- 1 · reveal on scroll --------------------------------------- */
  var revealed = null;
  function observe(root) {
    if (!('IntersectionObserver' in window)) return;
    if (!revealed) {
      revealed = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) { e.target.classList.add('is-in'); revealed.unobserve(e.target); }
        });
      }, { rootMargin: '0px 0px -6% 0px', threshold: 0.05 });
    }
    (root || document).querySelectorAll('.o-rv:not(.is-in)').forEach(function (n) { revealed.observe(n); });
  }

  /* ---------- 2 · transparent header ------------------------------------- */
  /* Deliberately plural. A store can end up with more than one header in the
     DOM — a sticky-header app that clones the real one, a leftover duplicate
     from a previous theme, a drawer header. Styling only the first match leaves
     the other one untouched, so it slides in on scroll wearing the theme's own
     white background. Every header-shaped element is adopted, and a watcher
     catches any that are added to the page later. */
  var headers = [];
  var bodyWatcher = null;

  var HEADER_SELECTORS =
    '#shopify-section-header,.shopify-section-header,.section-header,' +
    '.header-wrapper,sticky-header,header.header,body > header';

  /* The header's own chrome only. Dropdown submenus, the mobile menu drawer, the
     search modal and the localization disclosure all carry Dawn's `gradient` and
     `color-scheme-N` classes too — clearing those would leave a see-through menu
     floating over the page, so they are excluded by name. */
  var NOT_POPUP = ':not([class*="submenu"]):not([class*="drawer"]):not([class*="modal"])' +
                  ':not([class*="popup"]):not([class*="disclosure"]):not([class*="notification"])';
  var INNER = 'sticky-header,.header-wrapper,.header,' +
              '.gradient' + NOT_POPUP + ',[class*="color-scheme"]' + NOT_POPUP;

  function selectorList() {
    var extra = window.OPUS_HEADER_SELECTOR ? window.OPUS_HEADER_SELECTOR + ',' : '';
    return (extra + HEADER_SELECTORS).split(',');
  }

  function findHeaders() {
    var found = [];
    selectorList().forEach(function (s) {
      s = s.trim(); if (!s) return;
      var list;
      try { list = document.querySelectorAll(s); } catch (e) { return; }
      Array.prototype.forEach.call(list, function (el) {
        if (found.indexOf(el) === -1) found.push(el);
      });
    });
    /* keep the outermost wrapper of each header, not its inner rows */
    return found.filter(function (el) {
      return !found.some(function (o) { return o !== el && o.contains(el); });
    });
  }

  /* A stylesheet `!important` beats a normal inline style, but it loses to an
     ID-scoped `!important` rule and to anything a foreign script writes with
     setProperty(..., 'important'). Writing our own inline !important is the one
     thing nothing else can outrank — this is what makes the header transparent
     even on a theme carrying rules we have never seen. */
  /* While the mobile drawer or the search panel is open the header has to sit on
     a solid surface, or you get dark type on the drawer's own background. In that
     state we take our hands off and let the theme paint it. */
  function panelOpen(el) {
    var r = document.documentElement, b = document.body;
    return el.classList.contains('nav-open') || el.classList.contains('menu-open') ||
           r.classList.contains('nav-open') || r.classList.contains('search-open') ||
           (b && (b.classList.contains('nav-open') || b.classList.contains('search-open')));
  }

  function scrub(el) {
    var parts = [el], inner = el.querySelectorAll(INNER), i;
    for (i = 0; i < inner.length; i++) parts.push(inner[i]);
    var open = panelOpen(el);
    for (i = 0; i < parts.length; i++) {
      var s = parts[i].style;
      if (open) {
        s.removeProperty('background');
        s.removeProperty('background-image');
        s.removeProperty('border');
        s.removeProperty('box-shadow');
        s.removeProperty('backdrop-filter');
        s.removeProperty('-webkit-backdrop-filter');
        continue;
      }
      s.setProperty('background', 'transparent', 'important');
      s.setProperty('background-image', 'none', 'important');
      s.setProperty('border', '0', 'important');
      s.setProperty('box-shadow', 'none', 'important');
      s.setProperty('backdrop-filter', 'none', 'important');
      s.setProperty('-webkit-backdrop-filter', 'none', 'important');
    }
    /* a duplicate stays hidden even if its own script keeps re-showing it */
    if (el.getAttribute('data-opus-dupe') === '1') {
      el.style.setProperty('display', 'none', 'important');
    }
  }

  /* If something repaints the header after us — an old scroll script, an app —
     the change lands as a style or class mutation. Re-scrub when it does. */
  function guard(el) {
    if (!('MutationObserver' in window)) return;
    var busy = false;
    new MutationObserver(function () {
      if (busy) return;
      busy = true;
      requestAnimationFrame(function () { scrub(el); busy = false; });
    }).observe(el, { attributes: true, attributeFilter: ['style', 'class'], subtree: true });
  }

  function initLogo(header) {
    if (window.OPUS_LOGO === false) return;
    var logo =
      header.querySelector('.header__heading-link') ||
      header.querySelector('.header__heading') ||
      header.querySelector('[class*="logo"] a, a[class*="logo"]') ||
      header.querySelector('a[href="/"]');
    if (!logo) return;
    logo.classList.add('opus-hdr__logo');
    if (window.OPUS_LOGO_INVERT) logo.classList.add('opus-hdr__logo--invert');
    /* Only treat the header as "themed" if its row really is a grid with a
       `heading` area — that is what our column override can steer. Anything else
       (a flex bar, a custom header) gets the floating logo instead. */
    var row = header.querySelector('.header') || header.firstElementChild;
    var themed = false;
    if (row) {
      var cs = getComputedStyle(row);
      themed = cs.display.indexOf('grid') !== -1 &&
               String(cs.gridTemplateAreas).indexOf('heading') !== -1;
    }
    header.classList.add(themed ? 'opus-hdr--themed' : 'opus-hdr--float');
    if (window.OPUS_LOGO_SRC && !logo.dataset.opusLogo) {
      logo.dataset.opusLogo = '1';
      var img = document.createElement('img');
      img.src = window.OPUS_LOGO_SRC;
      img.alt = window.OPUS_LOGO_ALT || '';
      if (window.OPUS_LOGO_W) { img.width = window.OPUS_LOGO_W; }
      if (window.OPUS_LOGO_H) { img.height = window.OPUS_LOGO_H; }
      logo.textContent = '';
      logo.appendChild(img);
    }
  }

  function adopt(el) {
    /* Identity, never the class. A cloned header carries `.opus-hdr` and every
       data-attribute of the original, so a class check would mark the copy as
       already handled and leave it painted white. */
    if (!el || headers.indexOf(el) !== -1) return;
    el.classList.add('opus-hdr');
    /* Only the first header gets the sticky/z-index treatment. A second one is
       something the theme or an app positions itself — forcing it sticky would
       drop it wherever it happens to sit in the document. It still gets the
       background reset and the adaptive colour. */
    if (headers.length) el.classList.add('opus-hdr--extra');
    headers.push(el);
    initLogo(el);
    scrub(el);
    guard(el);
  }

  function measure() {
    if (!headers.length) return;
    /* the first header in document order is the one in normal flow — the one the
       hero has to tuck under. A clone is usually fixed and out of flow. */
    var h = headers[0].offsetHeight || 0;
    if (!h) { headers.forEach(function (el) { h = Math.max(h, el.offsetHeight || 0); }); }
    document.documentElement.style.setProperty('--opus-hdr-h', (h || 70) + 'px');
  }

  /* Some stores end up with a real header plus a duplicate stacked on top of it —
     a sticky-header app, or a leftover from a previous theme. Two headers drawn in
     the same place render every label twice. When two adopted headers are visible
     AND overlapping at the same left edge, the later one is a copy: hide it. */
  function dedupe() {
    var vis = headers.filter(function (el) {
      var r = el.getBoundingClientRect();
      return r.height > 0 && r.width > 0 && el.getAttribute('data-opus-dupe') !== '1';
    });
    if (vis.length < 2) return;
    var a = vis[0].getBoundingClientRect();
    for (var i = 1; i < vis.length; i++) {
      var b = vis[i].getBoundingClientRect();
      var overlap = Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top);
      if (overlap > Math.min(a.height, b.height) * 0.6 && Math.abs(a.left - b.left) < 4) {
        vis[i].setAttribute('data-opus-dupe', '1');
        vis[i].style.setProperty('display', 'none', 'important');
      }
    }
  }

  function headerState() {
    if (!headers.length) return;
    dedupe();
    var nodes = document.querySelectorAll('[data-opus-dark="true"]');
    headers.forEach(function (el) {
      var r = el.getBoundingClientRect();
      if (!r.height) return;
      /* Probe the middle of the header WHERE IT ACTUALLY IS, not a fixed offset
         from the top of the viewport. Stores with an announcement or utility bar
         above the header (country selector, free-shipping strip) sit that bar in
         the top ~40px, so a viewport-relative probe samples the bar instead of
         the section under the header. */
      var probe = r.top + Math.max(18, r.height / 2);
      var dark = false;
      if (!panelOpen(el)) {
        for (var i = 0; i < nodes.length; i++) {
          var q = nodes[i].getBoundingClientRect();
          if (q.top <= probe && q.bottom >= probe) { dark = true; break; }
        }
      }
      el.classList.toggle('opus-hdr--dark', dark);
    });
  }

  function watchForNewHeaders() {
    if (bodyWatcher || !('MutationObserver' in window) || !document.body) return;
    var pending = false;
    bodyWatcher = new MutationObserver(function () {
      if (pending) return;
      pending = true;
      requestAnimationFrame(function () {
        pending = false;
        headers = headers.filter(function (el) { return el.isConnected !== false; });
        var fresh = findHeaders().filter(function (el) { return headers.indexOf(el) === -1; });
        if (fresh.length) { fresh.forEach(adopt); measure(); headerState(); }
      });
    });
    bodyWatcher.observe(document.body, { childList: true, subtree: true });
  }

  function initHeader() {
    if (window.OPUS_TRANSPARENT_HEADER === false) return;
    findHeaders().forEach(adopt);
    if (!headers.length) return;
    measure();
    headerState();
    watchForNewHeaders();
  }

  /* ---------- 3 · card scrollers ----------------------------------------- */
  function cardStep(track) {
    var card = track.querySelector('.o-sys__c');
    if (!card) return track.clientWidth * 0.8;
    var gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || 0;
    return card.getBoundingClientRect().width + gap;
  }

  function syncNav(row) {
    var track = row.querySelector('[data-o-scroller]');
    var prev = row.querySelector('[data-o-prev]');
    var next = row.querySelector('[data-o-next]');
    if (!track || !prev || !next) return;
    var max = track.scrollWidth - track.clientWidth;
    prev.disabled = track.scrollLeft <= 2;
    next.disabled = track.scrollLeft >= max - 2;
  }

  function initScrollers(root) {
    (root || document).querySelectorAll('.o-sysrow').forEach(function (row) {
      if (row.dataset.oReady === '1') return;
      row.dataset.oReady = '1';
      var track = row.querySelector('[data-o-scroller]');
      if (!track) return;
      var prev = row.querySelector('[data-o-prev]');
      var next = row.querySelector('[data-o-next]');
      if (prev) prev.addEventListener('click', function () {
        track.scrollBy({ left: -cardStep(track), behavior: 'smooth' });
      });
      if (next) next.addEventListener('click', function () {
        track.scrollBy({ left: cardStep(track), behavior: 'smooth' });
      });
      track.addEventListener('scroll', function () { syncNav(row); }, { passive: true });
      window.addEventListener('resize', function () { syncNav(row); });
      syncNav(row);
    });
  }

  /* ---------- 4 · in-page links ------------------------------------------ */
  function initAnchors() {
    document.addEventListener('click', function (e) {
      var a = e.target.closest('.o-home a[href^="#"]');
      if (!a) return;
      var id = a.getAttribute('href').slice(1);
      if (!id) return;
      var target = document.getElementById(id);
      if (!target) return;
      e.preventDefault();
      var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    });
  }

  function init() {
    observe();
    initHeader();
    initScrollers();
    initAnchors();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.addEventListener('scroll', headerState, { passive: true });
  window.addEventListener('resize', function () { measure(); headerState(); });
  window.addEventListener('load', function () { initHeader(); measure(); headerState(); });

  /* keep everything correct while editing in the theme editor */
  document.addEventListener('shopify:section:load', function (e) {
    observe(e.target);
    initScrollers(e.target);
    initHeader();
    measure();
    headerState();
  });
  document.addEventListener('shopify:section:reorder', headerState);
  document.addEventListener('shopify:section:unload', headerState);
  document.addEventListener('shopify:section:select', headerState);
})();
